import { Col, Form, Input, Modal, Row, Switch, Tag } from 'antd';
import { Device, DeviceTag, RemoveTagPayload } from 'types/devices';
import { AddLabel } from './AddTag';
import { useState } from 'react';
import { removeDeviceTag, changeDeviceStatus } from 'api/devices.api';
import openNotification from 'hooks/notificationHook';
import { useAppDispatch } from 'hooks/reduxHooks';
import { doList } from 'store/slices/devicesSlice';
import * as Sentry from '@sentry/react';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';
import * as S from '../../pages/Global.styles';
import { ApiError } from 'types';
import { TableEditButton } from 'components/TableEditButton/TableEditButton';

export const EditDeviceModal = ({ device }: { device: Device }) => {
  const [showDeleteTagModal, setShowDeleteTagModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<DeviceTag>();
  const [removingTag, setRemovingTag] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useAppDispatch();

  const handleChangeDeviceStatus = async (checked: boolean) => {
    openNotification({
      type: 'warning',
      placement: 'top',
      message: 'Cambiar estatus',
      description: `¿Confirmas que deseas ${device.status ? 'Desactivar' : 'Activar'} el dispositivo?`,
      duration: null,
      showConfirmBtn: true,
      onConfirm: async () => {
        await changeDeviceStatus(device._id);
        setSwitchChecked(checked);
      },
      onClose: () => {
        setSwitchChecked(!checked);
      },
      confirmBtnLabel: `${device?.status ? 'Desactivar' : 'Activar'}`,
    });
  };

  const handleSwitchChange = (checked: boolean) => {
    handleChangeDeviceStatus(checked);
  };

  const deleteTag = async (tagId: string) => {
    setRemovingTag(true);
    const payload: RemoveTagPayload = {
      deviceId: device._id,
      tagId,
    };
    await removeDeviceTag(payload)
      .then(() => {
        setRemovingTag(false);
        dispatch(doList());
        notificationHook({
          type: 'success',
          message: 'Etiqueta eliminada con éxito.',
        });
      })
      .catch((error) => {
        setRemovingTag(false);
        const currentStatus = (error as ApiError).statusCode;
        notificationHook({
          type: 'error',
          message: statusMessages.devices[
            currentStatus ? currentStatus : 500
          ] as string,
        });
        Sentry.captureException(error);
      });
  };

  return (
    <>
      <TableEditButton
        dataCy="edit-device-button"
        onClick={() => {
          setIsEditing(true);
        }}
      />
      <Modal
        title="Editar dispositivo"
        open={isEditing}
        destroyOnHidden
        data-cy="edit-device-modal"
        onCancel={() => {
          setIsEditing(false);
        }}
        width="50vw"
        footer={null}
      >
        <>
          <Form labelCol={{ span: 5 }}>
            <Form.Item label="Número de serie">
              <Input disabled value={device.serialNumber} />
            </Form.Item>
            <Form.Item label="Nombre del dispositivo">
              <Input disabled value={device.configs.DeviceName} />
            </Form.Item>
            <Form.Item label="IP del dispositivo">
              <Input disabled value={device.properties.IPAddress} />
            </Form.Item>
            <Form.Item label="Etiquetas" data-cy="device-tags-edit-form">
              {device.tags && device.tags.length > 0 ? (
                <>
                  {device.tags.map((tag: DeviceTag) => (
                    <Tag
                      key={tag._id}
                      color={tag.tagColor}
                      variant="solid"
                      closable
                      data-cy="device-tag"
                      onClose={(e) => {
                        e.preventDefault();
                        setTagToDelete(tag);
                        setShowDeleteTagModal(true);
                      }}
                    >
                      {tag.tagName}
                    </Tag>
                  ))}
                </>
              ) : (
                <S.LightGreyText>Sin etiquetas</S.LightGreyText>
              )}
            </Form.Item>
            <Form.Item label="Activar dispositivo">
              <Switch
                onChange={handleSwitchChange}
                checked={switchChecked}
                id="status"
                data-cy="status-switch"
                disabled={!device}
              />
            </Form.Item>
          </Form>
          <AddLabel deviceId={device._id} />
        </>
      </Modal>
      <Modal
        title="¿Confirmas que deseas remover la etiqueta?"
        open={showDeleteTagModal}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={removingTag}
        onOk={() => {
          if (tagToDelete && tagToDelete._id) {
            deleteTag(tagToDelete._id);
            setShowDeleteTagModal(false);
          }
        }}
        onCancel={() => {
          setShowDeleteTagModal(false);
        }}
      >
        <Row>
          <Col xs={24}>
            {tagToDelete && device && (
              <p>
                Al confirmar, se eliminará la etiqueta{' '}
                <Tag color={tagToDelete.tagColor} variant="solid">
                  {tagToDelete.tagName}
                </Tag>
                del dispositivo <b>{device.configs.SerialNumber}</b>
              </p>
            )}
          </Col>
        </Row>
      </Modal>
    </>
  );
};
